import { createFileRoute, Link, stripSearchParams } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Search, Clock, ArrowRight } from "lucide-react";
import {
  listPublishedPosts,
  BLOG_CATEGORIES,
  categoryLabel,
  type BlogPostSummary,
} from "@/lib/blog.functions";
import { browserLocale } from "@/lib/locale";

const searchSchema = z.object({
  q: fallback(z.string(), "").default(""),
  category: fallback(z.string(), "trauma-recovery").default("trauma-recovery"),
});

export const blogQuery = (params: { q: string; category: string }) => ({
  queryKey: ["blog", "list", params],
  // On the server the language comes from the request; in the browser it comes
  // from the address, so the key can stay the same in both places.
  queryFn: () =>
    listPublishedPosts({
      data:
        typeof window === "undefined" ? params : { ...params, lang: browserLocale() },
    }),
  staleTime: 60_000,
});

export const Route = createFileRoute("/blog/")({
  validateSearch: zodValidator(searchSchema),
  search: { middlewares: [stripSearchParams({ q: "", category: "trauma-recovery" })] },
  loaderDeps: ({ search }) => ({ q: search.q, category: search.category }),
  loader: ({ context, deps }) => context.queryClient.ensureQueryData(blogQuery(deps)),
  head: () => ({
    meta: [
      { title: "Blog — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Survivor-led articles on trauma recovery, CPTSD, nervous system regulation, grounding, somatic therapy, sleep, anxiety, attachment and self-care.",
      },
      { property: "og:title", content: "Blog — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Survivor-led articles on trauma recovery, CPTSD, nervous system, grounding, somatic therapy and more.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://recoverytrauma.com/blog" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/blog" }],
  }),
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-2xl px-4 py-16 text-center">
      <h1 className="text-2xl font-semibold">Something went wrong loading the blog.</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {error instanceof Error ? error.message : "Please try again."}
      </p>
    </div>
  ),
  notFoundComponent: () => <div className="p-8 text-center">Not found</div>,
  component: BlogIndex,
});

function BlogIndex() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [qLocal, setQLocal] = useState(search.q);
  const { data: postsRaw } = useSuspenseQuery(blogQuery({ q: search.q, category: search.category }));
  const posts =
    search.category === ""
      ? [...postsRaw].sort((a, b) => {
          const aT = a.category === "trauma-recovery" ? 0 : 1;
          const bT = b.category === "trauma-recovery" ? 0 : 1;
          return aT - bT;
        })
      : postsRaw;

  function submitSearch(e: React.FormEvent) {
    e.preventDefault();
    navigate({ search: (prev: z.infer<typeof searchSchema>) => ({ ...prev, q: qLocal }) });
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:py-14">
      <header className="mb-8">
        <p className="text-xs font-medium uppercase tracking-wider text-primary">Blog</p>
        <h1 className="mt-2 text-3xl font-semibold text-foreground sm:text-4xl">
          Wellbeing writing for trauma survivors
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground sm:text-base">
          Free, survivor-led articles on trauma recovery, nervous system regulation and everything
          in between. New writing every week.
        </p>
      </header>

      <form onSubmit={submitSearch} className="mb-8">
        <div className="relative min-w-0">
          <Search className="pointer-events-none absolute start-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={qLocal}
            onChange={(e) => setQLocal(e.target.value)}
            placeholder="Search articles…"
            aria-label="Search articles"
            className="w-full rounded-full border border-border bg-background py-3 ps-11 pe-24 text-base outline-none focus:border-primary sm:text-sm"
          />
          <button
            type="submit"
            className="absolute end-1.5 top-1/2 -translate-y-1/2 rounded-full bg-primary px-4 py-2 text-xs font-medium text-primary-foreground transition hover:opacity-90"
          >
            Search
          </button>
        </div>
      </form>


      <nav className="mb-8 -mx-4 overflow-x-auto px-4 pb-2">
        <div className="flex gap-2">
          <Link
            to="/blog"
            search={{ q: search.q, category: "" }}
            className={
              "shrink-0 rounded-full border px-3.5 py-1.5 text-xs font-medium transition " +
              (search.category === ""
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-background text-muted-foreground hover:border-primary/40")
            }
          >
            All
          </Link>
          {BLOG_CATEGORIES.map((c) => (
            <Link
              key={c.slug}
              to="/blog/category/$category"
              params={{ category: c.slug }}
              className="shrink-0 rounded-full border border-border bg-background px-3.5 py-1.5 text-xs font-medium text-muted-foreground transition hover:border-primary/40"
            >
              {c.label}
            </Link>
          ))}
        </div>
      </nav>


      {posts.length === 0 ? (
        <p className="py-16 text-center text-sm text-muted-foreground">
          No articles match your search yet.
        </p>
      ) : (
        <section aria-labelledby="all-articles-heading">
          <h2
            id="all-articles-heading"
            className="mb-4 text-lg font-semibold text-foreground sm:text-xl"
          >
            All Articles
          </h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {posts.map((p) => (
              <BlogCard key={p.id} post={p} />
            ))}
          </div>
        </section>
      )}

    </div>
  );
}

export function BlogCard({ post }: { post: BlogPostSummary }) {
  return (
    <Link
      to="/blog/$slug"
      params={{ slug: post.slug }}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card transition hover:border-primary/40 hover:shadow-md"
    >
      {post.cover_image_url ? (
        <div className="aspect-[16/9] w-full overflow-hidden bg-muted">
          <img
            src={post.cover_image_url}
            alt=""
            className="h-full w-full object-cover transition group-hover:scale-105"
            loading="lazy"
          />
        </div>
      ) : (
        <div className="aspect-[16/9] w-full bg-gradient-to-br from-primary/10 to-primary/5" />
      )}
      <div className="flex flex-1 flex-col p-5">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-primary">
          {categoryLabel(post.category)}
        </p>
        <h3 className="mt-2 text-lg font-semibold text-foreground group-hover:text-primary">
          {post.title}
        </h3>
        <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{post.excerpt}</p>
        <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            {post.reading_time_minutes} min read
          </span>
          <span className="inline-flex items-center gap-1 font-medium text-primary opacity-0 transition group-hover:opacity-100">
            Read <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}
